import { AfterViewChecked, Component, ElementRef, EventEmitter, Inject, OnDestroy, OnInit, Output } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Subscription } from 'rxjs';
import {
  GuardarMargenesVesBody,
  GuardarMargenesVesResponse,
  MainRatesService,
  MargenesVesResponse,
  MotorTasasVes,
  ParFranja,
} from '../../services/main-rates.service';
import { erroresDe, mensajeSinPermiso, statusDe, TEXTO_SOLO_LECTURA } from './franjas-editor.rules';
import {
  etiquetaNivelGanancia,
  formatoPct,
  GANANCIA_SUGERIDA_PCT,
  GananciaVesClp,
  gananciaVesClp,
  posicionEnRegla,
} from './margen-ves-clp';
import * as MV from './margenes-ves.rules';

export interface OperadorData {
  operador: 'MANUAL' | 'BINANCE' | 'CBPAY';
  descuentoFiat: number;
  comision: number;
  comisionVesClp: number;
  rateVesUsdt: number;
  usdClp: number;
}

/** Datos con los que main-rates abre el modal. */
export interface EditOperatorModalData {
  operadorActivo: string;
  parametros: {
    manual: OperadorData;
    binance: OperadorData;
    cbpay: OperadorData;
  };
  /**
   * Puede cambiar márgenes y motor CLP↔VES: el mismo permiso que el editor de
   * franjas (`rates/franjas/permisos`, con respaldo en la lista del front).
   * Sin él, la sección «Márgenes CLP ↔ VES» se ve en solo lectura.
   */
  puedeEditarMargenes?: boolean;
}

/**
 * Paso de la sección «Márgenes CLP ↔ VES»:
 * cargando → viendo → editando → (relee lo guardado) confirmando → guardando
 * → (resultado) viendo. `error` = no se pudo leer `rates/margenes-ves` (se
 * puede reintentar).
 */
export type PasoMargenes = 'cargando' | 'error' | 'viendo' | 'editando' | 'confirmando' | 'guardando';

/** Resultado del último guardado de márgenes (o de su error), a la vista en la sección. */
export type ResultadoMargenes = MV.ResultadoMargenes;

/** Pestañas del modal (03/11/2026). */
export type PestanaModal = 'parametros' | 'margenes';

/** Adónde va el foco después de cada paso (lo mueve `ngAfterViewChecked`). */
const FOCO_CONFIRMACION = '#mv-confirmar-titulo';
const FOCO_PRIMER_INPUT = '.mv-tabla input.mv-input';
const FOCO_RESULTADO = '#mv-resultado';
/** Focos dentro de la pestaña de márgenes: pedirlos la pone a la vista. */
const FOCOS_MARGENES = new Set([FOCO_CONFIRMACION, FOCO_PRIMER_INPUT, FOCO_RESULTADO]);

@Component({
  selector: 'app-edit-operator-modal',
  templateUrl: './edit-operator-modal.component.html',
  styleUrls: ['./edit-operator-modal.component.sass']
})
export class EditOperatorModalComponent implements OnInit, OnDestroy, AfterViewChecked {
  form: FormGroup;
  operadores = [
    { value: 'MANUAL', label: 'Manual' },
    { value: 'BINANCE', label: 'Binance' },
    { value: 'CBPAY', label: 'CBPAY' }
  ];

  /**
   * Pestaña a la vista (03/11/2026): con los márgenes el modal ya no cabía en
   * la pantalla. Las dos siguen en el DOM (`[hidden]`), así que cambiar de
   * pestaña no pierde lo escrito ni corta un guardado en curso.
   */
  pestana: PestanaModal = 'parametros';
  readonly pestanas: ReadonlyArray<{ valor: PestanaModal; etiqueta: string }> = [
    { valor: 'parametros', etiqueta: 'Parámetros del operador' },
    { valor: 'margenes', etiqueta: 'Márgenes CLP ↔ VES' },
  ];

  /**
   * Se emite al guardar los márgenes o el motor con éxito: main-rates refresca
   * las píldoras del spot cruzado y las tarjetas de tasas sin esperar a que se
   * cierre el modal (los márgenes se guardan aparte de los parámetros). `null`
   * = el PUT no respondió bien, pero la relectura muestra que se guardó.
   */
  @Output() margenesGuardados = new EventEmitter<GuardarMargenesVesResponse | null>();

  // ───── Márgenes CLP ↔ VES (motor de márgenes, 03/11/2026) ─────
  readonly rutas = MV.RUTAS_VES;
  readonly campos = MV.CAMPOS_MARGEN;
  readonly motores = MV.MOTORES_VES;
  readonly motivoMargenesMax = MV.MOTIVO_MARGENES_MAX;
  readonly textoLegadoNoAfecta = MV.TEXTO_LEGADO_NO_AFECTA;
  readonly textoRepartoAutomatico = MV.TEXTO_REPARTO_AUTOMATICO;
  readonly textoSinSpot = MV.TEXTO_SIN_SPOT;
  readonly textoSoloLectura = TEXTO_SOLO_LECTURA;
  readonly textoAntesNoRige = MV.TEXTO_ANTES_NO_RIGE;
  readonly textoPasaTope = MV.TEXTO_PASA_TOPE_MARGENES;

  pasoMargenes: PasoMargenes = 'cargando';
  /** Lo último leído de `rates/margenes-ves` (lo guardado). */
  margenesVes: MargenesVesResponse | null = null;
  /** Cuándo se leyó `margenesVes`: el spot cruzado es el de esa hora. */
  margenesLeidosEn: Date | null = null;
  errorCargaMargenes: string | null = null;
  /** Lo que se edita. En «viendo» es una copia de lo guardado. */
  borradorMargenes: MV.BorradorMargenes | null = null;
  private inicialMargenes: MV.BorradorMargenes | null = null;
  /**
   * Versión de lo guardado (del GET, si el back la manda). Viaja en el PUT: si
   * otro operador guardó antes, el back responde 409 y no pisa nada.
   */
  private versionMargenes: string | null = null;
  motivoMargenes = '';
  resultadoMargenes: ResultadoMargenes | null = null;
  /** Otro operador cambió lo guardado mientras se editaba (aviso ámbar). */
  avisoMargenes: string | null = null;
  /** «Revisar cambios» está releyendo lo guardado antes de la confirmación. */
  revisandoMargenes = false;
  /** «Reintentar aplicar» en vuelo (la sección sigue en «viendo»). */
  reaplicando = false;
  /** Lo último que se mandó en el PUT: «Reintentar aplicar» lo reenvía. */
  private ultimoCuerpo: GuardarMargenesVesBody | null = null;
  /** El back respondió 403 al guardar: la sección queda en solo lectura. */
  private sinPermisoServidor = false;
  /**
   * Franjas activas por ruta (de `rates/franjas/estado`) para la vista previa
   * cuando el GET de márgenes no trae el reparto de una ruta.
   */
  private franjasRespaldo: Partial<Record<MV.RutaVes, MV.FranjaBase[]>> = {};
  private pidiendoRespaldo = false;
  /** Foco a mover en el próximo render (selector dentro del modal). */
  private focoPendiente: string | null = null;
  private subMargenes?: Subscription;
  private subGuardado?: Subscription;
  private subOperador?: Subscription;

  constructor(
    private fb: FormBuilder,
    public dialogRef: MatDialogRef<EditOperatorModalComponent>,
    @Inject(MAT_DIALOG_DATA) public data: EditOperatorModalData,
    private mainRates: MainRatesService,
    private host: ElementRef<HTMLElement>,
  ) {
    this.form = this.fb.group({
      operador: ['MANUAL', Validators.required],
      descuentoFiat: [0, [Validators.required, Validators.min(0.0001)]],
      comision: [0, [Validators.required, Validators.min(0.01)]],
      comisionVesClp: [0, [Validators.required, Validators.min(0)]],
      rateVesUsdt: [0, [Validators.required, Validators.min(0.01)]],
      usdClp: [0, [Validators.required, Validators.min(0.01)]]
    });
  }

  ngOnInit(): void {
    // Cargar valores del operador activo por defecto
    const operadorInicial = this.data.operadorActivo.toUpperCase() as 'MANUAL' | 'BINANCE' | 'CBPAY';
    this.form.patchValue({ operador: operadorInicial });
    this.cargarParametrosOperador(operadorInicial);

    // Escuchar cambios en el selector de operador
    this.subOperador = this.form.get('operador')?.valueChanges.subscribe((operador) => {
      this.cargarParametrosOperador(operador);
    });

    this.cargarMargenes();
  }

  ngOnDestroy(): void {
    this.subMargenes?.unsubscribe();
    this.subGuardado?.unsubscribe();
    this.subOperador?.unsubscribe();
  }

  /**
   * Mueve el foco pedido (confirmación, primer campo o resultado) apenas el
   * paso nuevo está pintado. Una sola vez por pedido.
   */
  ngAfterViewChecked(): void {
    const selector = this.focoPendiente;
    if (!selector) return;
    this.focoPendiente = null;
    this.host?.nativeElement?.querySelector<HTMLElement>(selector)?.focus();
  }

  private pedirFoco(selector: string): void {
    this.focoPendiente = selector;
    // Un paso de márgenes (confirmación, editar, resultado) se ve en su pestaña:
    // oculta, el foco no llegaría.
    if (FOCOS_MARGENES.has(selector)) this.pestana = 'margenes';
  }

  /** Muestra una pestaña. Con `enfocar`, el foco va a su botón. */
  elegirPestana(p: PestanaModal, enfocar = false): void {
    this.pestana = p;
    if (enfocar) this.pedirFoco(`#eom-tab-${p}`);
  }

  /** Flechas, Inicio y Fin entre las pestañas (patrón de pestañas de WAI-ARIA). */
  teclaPestanas(ev: KeyboardEvent): void {
    const n = this.pestanas.length;
    const i = this.pestanas.findIndex((p) => p.valor === this.pestana);
    const destino =
      ev.key === 'ArrowRight' ? (i + 1) % n
      : ev.key === 'ArrowLeft' ? (i - 1 + n) % n
      : ev.key === 'Home' ? 0
      : ev.key === 'End' ? n - 1
      : -1;
    if (destino < 0) return;
    ev.preventDefault();
    this.elegirPestana(this.pestanas[destino].valor, true);
  }

  /**
   * Carga los parámetros del operador seleccionado
   */
  cargarParametrosOperador(operador: 'MANUAL' | 'BINANCE' | 'CBPAY'): void {
    let params: OperadorData;

    switch (operador) {
      case 'MANUAL':
        params = this.data.parametros.manual;
        // En MANUAL, el rate es editable
        this.form.get('rateVesUsdt')?.enable();
        break;
      case 'BINANCE':
        params = this.data.parametros.binance;
        // En BINANCE, el rate es automático
        this.form.get('rateVesUsdt')?.disable();
        break;
      case 'CBPAY':
        params = this.data.parametros.cbpay;
        // En CBPAY, el rate es automático
        this.form.get('rateVesUsdt')?.disable();
        break;
    }

    this.form.patchValue({
      descuentoFiat: params.descuentoFiat,
      comision: params.comision,
      comisionVesClp: params.comisionVesClp || 0,
      rateVesUsdt: params.rateVesUsdt,
      usdClp: params.usdClp
    });
  }

  /**
   * Verifica si el rate VES/USDT es editable
   */
  get isRateEditable(): boolean {
    return this.form.get('operador')?.value === 'MANUAL';
  }

  readonly gananciaSugeridaPct = GANANCIA_SUGERIDA_PCT;

  /** Marcas de la regla (−1 %, 0, +0,5 %, +2 %) con su posición. */
  readonly marcasRegla = [
    { texto: '−1 %', pos: posicionEnRegla(-1) },
    { texto: '0', pos: posicionEnRegla(0) },
    { texto: '+0,5 %', pos: posicionEnRegla(0.5) },
    { texto: '+2 %', pos: posicionEnRegla(2) },
  ];

  /** «2,33»: dos decimales con coma (es-CL). */
  numero(n: number): string {
    return Number(n).toLocaleString('es-CL', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }

  etiquetaNivel(g: GananciaVesClp): string {
    return etiquetaNivelGanancia(g.nivel);
  }

  pct(n: number): string {
    return formatoPct(n);
  }

  posicion(g: GananciaVesClp): number {
    return posicionEnRegla(g.gananciaPct);
  }

  /**
   * Ganancia real de VES→CLP con lo que hay escrito en el formulario, en vivo.
   * Solo informativa: ver `margen-ves-clp.ts`.
   */
  get gananciaVesClp(): GananciaVesClp | null {
    const v = this.form.getRawValue();
    return gananciaVesClp(v.rateVesUsdt, v.usdClp, v.comision, v.comisionVesClp);
  }

  /**
   * Guarda los cambios
   */
  onSave(): void {
    // Los márgenes se guardan con su propio botón: con cambios de márgenes a
    // medio camino, este «Guardar» no corre (se perderían al cerrar). Tampoco
    // con un PUT de márgenes en vuelo («Reintentar aplicar»): cerrar lo cortaría.
    if (this.margenesPendientes || this.guardandoMargenes) return;
    if (this.form.valid) {
      const formValue = this.form.getRawValue(); // getRawValue incluye campos disabled

      // Preparar datos para enviar al backend
      const updateData = {
        operador: formValue.operador,
        descuentoFiat: formValue.descuentoFiat,
        comision: formValue.comision,
        comisionVesClp: formValue.comisionVesClp,
        usdClp: formValue.usdClp
      };

      // Solo incluir rateVesUsdt si es MANUAL (editable)
      if (formValue.operador === 'MANUAL') {
        Object.assign(updateData, { rateVesUsdt: formValue.rateVesUsdt });
      }

      this.dialogRef.close(updateData);
    }
  }

  /**
   * Cancela y cierra el modal
   */
  onCancel(): void {
    // Mientras se guardan los márgenes no se cierra (el resultado se muestra acá).
    if (this.guardandoMargenes) return;
    this.dialogRef.close();
  }

  /**
   * Obtiene el mensaje de error para un campo
   */
  getErrorMessage(fieldName: string): string {
    const field = this.form.get(fieldName);

    if (field?.hasError('required')) {
      return 'Este campo es requerido';
    }

    if (field?.hasError('min')) {
      const minValue = field.errors?.['min']?.min;
      if (minValue === 0) {
        return 'El valor debe ser mayor o igual a 0';
      } else if (minValue === 0.01) {
        return 'El valor debe ser mayor a 0';
      }
      return `El valor debe ser mayor o igual a ${minValue}`;
    }

    return '';
  }

  // ───────────────────────── Márgenes CLP ↔ VES ─────────────────────────

  /** Puede editar márgenes y motor (permiso del panel y el back no dijo que no). */
  get puedeEditarMargenes(): boolean {
    return !!this.data.puedeEditarMargenes && !this.sinPermisoServidor;
  }

  get margenesSoloLectura(): boolean {
    return !this.puedeEditarMargenes;
  }

  /** Motor GUARDADO (el que rige hoy). Null mientras no se leyó. */
  get motorGuardado(): MotorTasasVes | null {
    return this.margenesVes?.motor ?? null;
  }

  /** Rige el motor Márgenes: la nota de los campos de siempre y sin la «Ganancia real VES → CLP». */
  get motorMargenesActivo(): boolean {
    return this.motorGuardado === 'MARGENES';
  }

  get limiteMargen(): number {
    return this.margenesVes?.limiteMaximoPct ?? MV.LIMITE_MARGEN_PCT;
  }

  get editandoMargenes(): boolean {
    return this.pasoMargenes === 'editando';
  }

  /** Confirmando o guardando: se muestra el antes → después. */
  get enConfirmacion(): boolean {
    return this.pasoMargenes === 'confirmando' || this.pasoMargenes === 'guardando';
  }

  /** Un PUT en vuelo (confirmación o «Reintentar aplicar»): no se cierra ni se edita. */
  get guardandoMargenes(): boolean {
    return this.pasoMargenes === 'guardando' || this.reaplicando;
  }

  /** Con LEGADO guardado, los márgenes de «antes» están en la base pero no se cobran. */
  get antesNoRige(): boolean {
    return this.inicialMargenes?.motor === 'LEGADO';
  }

  /** «14:05:09»: la hora en que se leyó el spot cruzado (y lo guardado). */
  get horaLectura(): string | null {
    return MV.horaLectura(this.margenesLeidosEn);
  }

  get puedeReintentarAplicar(): boolean {
    return (
      !!this.resultadoMargenes?.reintentar &&
      !!this.ultimoCuerpo &&
      this.pasoMargenes === 'viendo' &&
      !this.reaplicando &&
      this.puedeEditarMargenes
    );
  }

  get hayCambiosMargenes(): boolean {
    return MV.hayCambiosMargenes(this.borradorMargenes, this.inicialMargenes);
  }

  /**
   * Hay cambios de márgenes sin guardar (o confirmándose o guardándose). El
   * «Guardar parámetros» de abajo no los incluye: mientras tanto queda quieto.
   */
  get margenesPendientes(): boolean {
    return this.enConfirmacion || (this.editandoMargenes && this.hayCambiosMargenes);
  }

  get erroresMargenes(): string[] {
    return MV.erroresMargenes(this.borradorMargenes, this.limiteMargen);
  }

  erroresRuta(ruta: MV.RutaVes): MV.ErroresRuta {
    return MV.erroresRuta(this.borradorMargenes?.[ruta], this.limiteMargen);
  }

  get motivoMargenesValido(): boolean {
    return MV.motivoMargenesValido(this.motivoMargenes);
  }

  get puedeRevisarMargenes(): boolean {
    return this.editandoMargenes && this.puedeEditarMargenes && this.hayCambiosMargenes && !this.erroresMargenes.length;
  }

  /** Ids de los errores de un campo (el suyo y el del rango de la ruta), para `aria-describedby`. */
  idsErrorCampo(ruta: MV.RutaVes, campo: MV.CampoMargen): string | null {
    const e = this.erroresRuta(ruta);
    const ids = [e[campo] ? `mv-error-${ruta}-${campo}` : null, e.rango ? `mv-error-${ruta}-rango` : null].filter(Boolean);
    return ids.length ? ids.join(' ') : null;
  }

  /** Por qué «Revisar cambios» está quieto (visible junto al botón). */
  get motivoNoRevisar(): string {
    if (!this.hayCambiosMargenes) return 'No hay cambios que guardar.';
    if (this.erroresMargenes.length) return 'Corrige los valores marcados en rojo.';
    return '';
  }

  get puedeConfirmarMargenes(): boolean {
    return (
      this.pasoMargenes === 'confirmando' &&
      this.puedeEditarMargenes &&
      !this.erroresMargenes.length &&
      this.motivoMargenesValido
    );
  }

  /** Valor guardado de un margen (modo «viendo»). */
  valorGuardado(ruta: MV.RutaVes, campo: MV.CampoMargen): number | null {
    return this.inicialMargenes?.[ruta]?.[campo] ?? null;
  }

  valorBorrador(ruta: MV.RutaVes, campo: MV.CampoMargen): number | null {
    return this.borradorMargenes?.[ruta]?.[campo] ?? null;
  }

  /** Antes → después de los 4 márgenes y el motor (paso de confirmación). */
  get resumenCambiosMargenes(): MV.ResumenCambiosMargenes | null {
    if (!this.borradorMargenes || !this.inicialMargenes) return null;
    return MV.resumenCambiosMargenes(this.inicialMargenes, this.borradorMargenes);
  }

  /** El motor que queda al guardar. */
  get motorFinal(): MotorTasasVes | null {
    return this.borradorMargenes?.motor ?? null;
  }

  get cambiaMotor(): boolean {
    return !!this.borradorMargenes && !!this.inicialMargenes && this.borradorMargenes.motor !== this.inicialMargenes.motor;
  }

  /** Franjas de una ruta para repartir: las del GET o, si no vino el reparto, las activas de `franjas/estado`. */
  private franjasDeRuta(ruta: MV.RutaVes): MV.FranjaBase[] {
    const delGet = this.margenesVes?.tramos?.[ruta] ?? [];
    return delGet.length ? delGet : this.franjasRespaldo[ruta] ?? [];
  }

  /** Vista previa del reparto por franja con los márgenes del borrador (espejo del back). */
  repartoNuevo(par: ParFranja): MV.FilaReparto[] {
    const ruta = MV.rutaDePar(par);
    return MV.repartoConMargenes(
      par,
      this.franjasDeRuta(ruta),
      this.borradorMargenes?.[ruta],
      MV.spotDeRuta(par, this.margenesVes?.spotCruzado),
    );
  }

  /** El reparto con los márgenes guardados, tal como lo calculó el back. */
  repartoActual(par: ParFranja): MV.FilaReparto[] {
    return MV.repartoGuardado(par, this.margenesVes?.tramos?.[MV.rutaDePar(par)]);
  }

  etiquetaMotor(m: MotorTasasVes | null | undefined): string {
    return MV.etiquetaMotor(m);
  }

  formatoMargen(x: number | null | undefined): string {
    return MV.formatoMargen(x);
  }

  formatoTasa(x: number | null | undefined): string {
    return MV.formatoTasaVes(x);
  }

  textoTasaCliente(x: number | null | undefined): string {
    return MV.textoTasaCliente(x);
  }

  textoTasaRuta(par: ParFranja, x: number | null | undefined): string {
    return MV.textoTasaRuta(par, x);
  }

  rangoClp(desde: number | null | undefined, hasta: number | null | undefined): string {
    return MV.rangoClp(desde, hasta);
  }

  /**
   * Lee lo guardado (`GET rates/margenes-ves`). `conservarResultado`: al
   * releer después de guardar no se borra el aviso del resultado.
   */
  cargarMargenes(conservarResultado = false): void {
    this.pasoMargenes = 'cargando';
    this.errorCargaMargenes = null;
    this.avisoMargenes = null;
    this.revisandoMargenes = false;
    if (!conservarResultado) this.resultadoMargenes = null;
    // Una lectura nueva reemplaza a la que estuviera en vuelo (respuestas fuera de orden).
    this.subMargenes?.unsubscribe();
    this.subMargenes = this.mainRates.obtenerMargenesVes().subscribe({
      next: (r) => {
        const m = MV.normalizarMargenesVes(r);
        if (!m) {
          this.ponerErrorCarga(MV.TEXTO_MARGENES_NO_DISPONIBLE);
          return;
        }
        this.ponerGuardado(m);
        this.borradorMargenes = MV.clonarBorradorMargenes(this.inicialMargenes!);
        this.motivoMargenes = '';
        this.pasoMargenes = 'viendo';
      },
      error: (err) => {
        const status = statusDe(err);
        this.ponerErrorCarga(
          status === 404 || status === 503 ? MV.TEXTO_MARGENES_NO_DISPONIBLE : erroresDe(err).join(' '),
        );
      },
    });
  }

  /** Lo leído del GET pasa a ser «lo guardado»: valores, motor, versión, spot y su hora. */
  private ponerGuardado(m: MargenesVesResponse): void {
    this.margenesVes = m;
    this.inicialMargenes = MV.borradorMargenesDesde(m);
    this.versionMargenes = m.version ?? null;
    this.margenesLeidosEn = new Date();
    this.pedirFranjasRespaldo(m);
  }

  private ponerErrorCarga(texto: string): void {
    this.margenesVes = null;
    this.inicialMargenes = null;
    this.borradorMargenes = null;
    this.versionMargenes = null;
    this.errorCargaMargenes = texto;
    this.pasoMargenes = 'error';
  }

  /** Si alguna ruta vino sin reparto, sus franjas activas para la vista previa (una vez por modal). */
  private pedirFranjasRespaldo(m: MargenesVesResponse): void {
    const faltan = this.rutas.filter((r) => !m.tramos[r.ruta].length && !this.franjasRespaldo[r.ruta]);
    if (!faltan.length || this.pidiendoRespaldo) return;
    this.pidiendoRespaldo = true;
    this.mainRates.obtenerEstadoFranjas().subscribe({
      next: (e) => {
        this.pidiendoRespaldo = false;
        faltan.forEach((r) => (this.franjasRespaldo[r.ruta] = MV.franjasActivasDeEstado(e, r.par)));
      },
      // Sin respaldo, la vista previa de esa ruta muestra solo los márgenes.
      error: () => (this.pidiendoRespaldo = false),
    });
  }

  /** «Cambiar márgenes o motor»: de «viendo» a «editando». */
  editarMargenes(): void {
    if (!this.puedeEditarMargenes || this.pasoMargenes !== 'viendo' || this.reaplicando || !this.inicialMargenes) return;
    this.borradorMargenes = MV.clonarBorradorMargenes(this.inicialMargenes);
    this.motivoMargenes = '';
    this.resultadoMargenes = null;
    this.avisoMargenes = null;
    this.pasoMargenes = 'editando';
    this.pedirFoco(FOCO_PRIMER_INPUT);
  }

  cambiarMargen(ruta: MV.RutaVes, campo: MV.CampoMargen, valor: unknown): void {
    if (!this.editandoMargenes || !this.borradorMargenes) return;
    const b = MV.clonarBorradorMargenes(this.borradorMargenes);
    b[ruta][campo] = valor === '' || valor == null ? null : Number(valor);
    this.borradorMargenes = b;
  }

  elegirMotor(motor: MotorTasasVes): void {
    if (!this.editandoMargenes || !this.borradorMargenes) return;
    this.borradorMargenes = { ...this.borradorMargenes, motor };
  }

  /** «Descartar»: vuelve a lo guardado. */
  descartarMargenes(): void {
    if (this.guardandoMargenes || !this.inicialMargenes) return;
    // Si «Revisar cambios» estaba releyendo, esa lectura ya no importa.
    if (this.revisandoMargenes) {
      this.subMargenes?.unsubscribe();
      this.revisandoMargenes = false;
    }
    this.borradorMargenes = MV.clonarBorradorMargenes(this.inicialMargenes);
    this.motivoMargenes = '';
    this.avisoMargenes = null;
    this.pasoMargenes = 'viendo';
  }

  /**
   * «Revisar cambios»: ANTES de la confirmación se relee lo guardado (spot
   * cruzado fresco, valores, motor, versión y reparto). Si otro operador lo
   * cambió mientras se editaba, se avisa y el borrador se rebasa: lo que este
   * operador tocó queda como lo escribió y lo demás toma lo guardado nuevo.
   * Todavía no se guarda nada.
   */
  revisarMargenes(): void {
    if (!this.puedeRevisarMargenes || this.revisandoMargenes) return;
    this.revisandoMargenes = true;
    this.resultadoMargenes = null;
    this.subMargenes?.unsubscribe();
    this.subMargenes = this.mainRates.obtenerMargenesVes().subscribe({
      next: (r) => {
        this.revisandoMargenes = false;
        const m = MV.normalizarMargenesVes(r);
        if (!m) {
          this.resultadoMargenes = { tipo: 'error', textos: [MV.TEXTO_MARGENES_NO_DISPONIBLE] };
          return;
        }
        if (this.pasoMargenes !== 'editando') return;
        this.rebasarSobre(m);
        // Tras el rebase puede no quedar nada que guardar (o quedar un cruce): se sigue editando.
        if (!this.puedeRevisarMargenes) {
          this.pedirFoco(FOCO_PRIMER_INPUT);
          return;
        }
        this.pasoMargenes = 'confirmando';
        this.pedirFoco(FOCO_CONFIRMACION);
      },
      error: (err) => {
        this.revisandoMargenes = false;
        this.resultadoMargenes = { tipo: 'error', textos: [MV.TEXTO_NO_SE_PUDO_REVISAR, ...erroresDe(err)] };
        this.pedirFoco(FOCO_RESULTADO);
      },
    });
  }

  /**
   * Lo guardado se releyó y pasa a ser el «antes». Si otro operador lo cambió
   * frente a lo que este modal había cargado, el borrador se rebasa
   * (`MV.rebasarBorrador`) y queda el aviso. Devuelve si había cambiado.
   */
  private rebasarSobre(m: MargenesVesResponse): boolean {
    const cargado = this.inicialMargenes;
    const guardado = MV.borradorMargenesDesde(m);
    const cambio = !!cargado && MV.hayCambiosMargenes(guardado, cargado);
    if (this.borradorMargenes && cargado) {
      this.borradorMargenes = MV.rebasarBorrador(this.borradorMargenes, cargado, guardado);
    }
    this.ponerGuardado(m);
    if (cambio) this.avisoMargenes = MV.TEXTO_OTRO_OPERADOR;
    return cambio;
  }

  volverAEditarMargenes(): void {
    if (this.pasoMargenes !== 'confirmando') return;
    this.pasoMargenes = 'editando';
    this.pedirFoco(FOCO_PRIMER_INPUT);
  }

  /** «Confirmar y guardar»: recién acá sale el PUT (con la versión de lo leído). */
  confirmarMargenes(): void {
    if (!this.puedeConfirmarMargenes || !this.borradorMargenes) return;
    const cuerpo = MV.cuerpoGuardarMargenes(
      this.borradorMargenes,
      this.inicialMargenes,
      this.motivoMargenes,
      this.limiteMargen,
      this.versionMargenes,
    );
    if (!cuerpo) return;
    this.enviarMargenes(cuerpo, false);
  }

  /**
   * «Reintentar aplicar»: con MARGENES alguna ruta no se grabó. Reenvía el
   * mismo cuerpo del último guardado, con la versión de ahora, aunque no haya
   * cambios: el back vuelve a grabar las tasas (si otro operador guardó en el
   * medio, 409 y no se pisa nada).
   */
  reintentarAplicar(): void {
    if (!this.puedeReintentarAplicar || !this.ultimoCuerpo) return;
    this.enviarMargenes(MV.conVersionMargenes(this.ultimoCuerpo, this.versionMargenes), true);
  }

  private enviarMargenes(cuerpo: GuardarMargenesVesBody, reintento: boolean): void {
    const motorAntes = this.inicialMargenes?.motor ?? null;
    const pedido = this.borradorMargenes ? MV.clonarBorradorMargenes(this.borradorMargenes) : null;
    this.ultimoCuerpo = cuerpo;
    this.resultadoMargenes = null;
    this.avisoMargenes = null;
    if (reintento) this.reaplicando = true;
    else this.pasoMargenes = 'guardando';
    this.bloquearCierre(true);
    this.subGuardado?.unsubscribe();
    this.subGuardado = this.mainRates.guardarMargenesVes(cuerpo).subscribe({
      next: (r) => this.alGuardarMargenes(r, motorAntes),
      error: (err) => (reintento ? this.alFallarReintento(err) : this.alFallarGuardado(err, pedido)),
    });
  }

  private alGuardarMargenes(r: GuardarMargenesVesResponse, motorAntes: MotorTasasVes | null): void {
    this.reaplicando = false;
    this.bloquearCierre(false);
    if (typeof r?.version === 'string' && r.version) this.versionMargenes = r.version;
    this.resultadoMargenes = MV.resultadoGuardado(r, motorAntes);
    this.margenesGuardados.emit(r);
    this.pedirFoco(FOCO_RESULTADO);
    // Se relee: valores, motor, versión y reparto quedan los del servidor.
    this.cargarMargenes(true);
  }

  /** 403 (prod, correo no autorizado): su mensaje y la sección en solo lectura. */
  private alNoTenerPermiso(texto: string): void {
    this.sinPermisoServidor = true;
    if (this.inicialMargenes) this.borradorMargenes = MV.clonarBorradorMargenes(this.inicialMargenes);
    this.resultadoMargenes = { tipo: 'error', textos: [texto] };
    this.pasoMargenes = 'viendo';
    this.pedirFoco(FOCO_RESULTADO);
  }

  private alFallarGuardado(err: any, pedido: MV.BorradorMargenes | null): void {
    const sinPermiso = mensajeSinPermiso(err);
    if (sinPermiso) {
      this.bloquearCierre(false);
      this.alNoTenerPermiso(sinPermiso);
      return;
    }
    const status = statusDe(err);
    // 409 (otro operador guardó antes) o 400 «Versión inválida»: no se guardó
    // nada. Su mensaje, se relee lo guardado y se vuelve a editar con lo
    // escrito rebasado sobre eso.
    if (MV.esConflictoDeVersion(err)) {
      const textos = [MV.mensajeConflictoMargenes(err), MV.TEXTO_TRAS_CONFLICTO];
      this.releerTrasError(
        pedido,
        () => {
          this.resultadoMargenes = { tipo: 'error', textos };
          this.pasoMargenes = 'editando';
          this.pedirFoco(FOCO_PRIMER_INPUT);
        },
        () => {
          this.resultadoMargenes = { tipo: 'error', textos: [textos[0]] };
          this.pasoMargenes = 'editando';
          this.pedirFoco(FOCO_PRIMER_INPUT);
        },
      );
      return;
    }
    // 400: el back no aceptó los valores. Se vuelve a editar con sus mensajes.
    if (status === 400) {
      this.bloquearCierre(false);
      this.resultadoMargenes = { tipo: 'error', textos: erroresDe(err) };
      this.pasoMargenes = 'editando';
      this.pedirFoco(FOCO_PRIMER_INPUT);
      return;
    }
    // 5xx (504 incluido) o sin conexión: el PUT pudo haber llegado. Se relee
    // y, si lo guardado ya es lo pedido, se trata como guardado.
    if (MV.esErrorIncierto(err)) {
      const motivo = status === 503 ? [MV.TEXTO_MARGENES_NO_DISPONIBLE] : erroresDe(err);
      this.releerTrasError(
        pedido,
        (otro) => {
          // Si además otro operador cambió algo, a editar (ya rebasado, con su
          // aviso); si no, lo guardado sigue igual y se puede reintentar.
          this.resultadoMargenes = { tipo: 'error', textos: otro ? motivo : [...motivo, MV.TEXTO_NO_SE_GUARDO] };
          this.pasoMargenes = otro ? 'editando' : 'confirmando';
          this.pedirFoco(otro ? FOCO_PRIMER_INPUT : FOCO_RESULTADO);
        },
        () => {
          this.resultadoMargenes = { tipo: 'error', textos: [MV.TEXTO_GUARDADO_INCIERTO] };
          this.pasoMargenes = 'confirmando';
          this.pedirFoco(FOCO_RESULTADO);
        },
      );
      return;
    }
    // Otro error: se queda en la confirmación para reintentar.
    this.bloquearCierre(false);
    this.resultadoMargenes = {
      tipo: 'error',
      textos: status === 404 ? [MV.TEXTO_MARGENES_NO_DISPONIBLE] : erroresDe(err),
    };
    this.pasoMargenes = 'confirmando';
    this.pedirFoco(FOCO_RESULTADO);
  }

  /**
   * Tras un choque de versión o un error incierto del PUT se relee lo guardado (sigue
   * «Guardando…» y el modal no se cierra hasta saber qué pasó):
   * - si ya es lo pedido, el guardado se da por hecho;
   * - si no, el borrador se rebasa sobre lo leído y sigue `noGuardado`
   *   (`otro` = otro operador lo había cambiado);
   * - si no se pudo leer, `sinLectura`.
   */
  private releerTrasError(
    pedido: MV.BorradorMargenes | null,
    noGuardado: (otro: boolean) => void,
    sinLectura: () => void,
  ): void {
    this.subMargenes?.unsubscribe();
    this.subMargenes = this.mainRates.obtenerMargenesVes().subscribe({
      next: (r) => {
        this.bloquearCierre(false);
        const m = MV.normalizarMargenesVes(r);
        if (!m) {
          sinLectura();
          return;
        }
        if (pedido && !MV.hayCambiosMargenes(pedido, MV.borradorMargenesDesde(m))) {
          this.ponerGuardado(m);
          this.borradorMargenes = MV.clonarBorradorMargenes(this.inicialMargenes!);
          this.motivoMargenes = '';
          this.resultadoMargenes = MV.resultadoYaGuardado(m.motor);
          this.pasoMargenes = 'viendo';
          this.margenesGuardados.emit(null);
          this.pedirFoco(FOCO_RESULTADO);
          return;
        }
        noGuardado(this.rebasarSobre(m));
      },
      error: () => {
        this.bloquearCierre(false);
        sinLectura();
      },
    });
  }

  /** «Reintentar aplicar» falló: su error (sin texto de protocolo) y se relee lo guardado. */
  private alFallarReintento(err: any): void {
    this.reaplicando = false;
    this.bloquearCierre(false);
    const sinPermiso = mensajeSinPermiso(err);
    if (sinPermiso) {
      this.alNoTenerPermiso(sinPermiso);
      return;
    }
    // Choque de versión (409 o 400 «Versión inválida»): su mensaje y se relee.
    this.resultadoMargenes = MV.esConflictoDeVersion(err)
      ? { tipo: 'error', textos: [MV.mensajeConflictoMargenes(err)] }
      : { tipo: 'error', textos: erroresDe(err), reintentar: statusDe(err) !== 400 };
    this.pedirFoco(FOCO_RESULTADO);
    this.cargarMargenes(true);
  }

  /** Mientras sale el PUT, ni Esc ni el clic afuera cierran el modal. */
  private bloquearCierre(bloquear: boolean): void {
    if (this.dialogRef) this.dialogRef.disableClose = bloquear;
  }

  trackRuta(_: number, r: MV.InfoRuta): string {
    return r.ruta;
  }

  trackFilaCambio(_: number, f: MV.FilaCambioRuta): string {
    return f.ruta;
  }

  trackFila(_: number, f: MV.FilaReparto): string {
    return f.clave;
  }
}
